import React from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import ActivityFormContainer from "../ActivityFormContainer";
import { ActivityState } from "@axio/graphql/generated/graphql";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { normalizeObjectives } from "../../../utils/objectives";

const typeMap: Record<string, string> = {
  AITutor: "Interactive Learning Experience",
  AxioResource: "Resource",
  Assignment: "Assignment",
  Project: "Project",
  Review: "Review",
  Assessment: "Assessment",
};

const EditActivityModalContainer = ({
  visible,
  onClose,
  onSave,
  error,
  isSubmitting,
  existingActivity = null,
  isLoading = false,
  courseId,
  activeModuleData,
}) => {
  const normalizedObjectives = React.useMemo(
    () =>
      normalizeObjectives({
        activityId: existingActivity?.id,
        objectives: existingActivity?.objectives,
      }),
    [existingActivity?.id, existingActivity?.objectives]
  );

  const formattedActivityData = React.useMemo(
    () =>
      existingActivity
        ? {
            title: existingActivity.title,
            description: existingActivity.description,
            targetAudience: existingActivity.targetAudience,
            id: existingActivity.id,
            type: existingActivity.type || typeMap[existingActivity.__typename || ""] || "Project",
            files: existingActivity.files || [],
            videoUrl: existingActivity.videoUrl || "",
            dueDate: existingActivity.dueDate || existingActivity.due_date || "",
            enableFileUpload: existingActivity.enableFileUpload || existingActivity.enableStudentFileUpload || false,
            context: existingActivity.context || "",
            // Resequence objectives on load to fix any existing gaps in sequence numbers
            rawObjectives: normalizedObjectives,
            visualization: existingActivity.visualization,
            visualizationId: existingActivity.visualization?.id || null,
            numberOfQuestions: existingActivity.numberOfQuestions,
            timeLimit: existingActivity.timeLimit,
            assessmentType: existingActivity.assessmentType,
            selectedAiTutorIds: Array.from(new Set(existingActivity.aiTutors?.map((aiTutor) => aiTutor.id))) || [],
            videoPool: existingActivity.videoPool
              ? existingActivity.videoPool.map((video) => ({
                  ...video,
                  isTeacherAdded: video.isTeacherAdded === true,
                }))
              : normalizedObjectives.length > 0
                ? normalizedObjectives.flatMap((objective, index) =>
                    (objective.videos ?? []).map((video) => ({
                      ...video,
                      objectiveText: objective.objective ?? "",
                      objectiveSequence: index + 1,
                    }))
                  )
                : [],
            worksheet: existingActivity.worksheet || null,
            scopeDocument: existingActivity.scopeDocument || "",
            deliverableTypes: existingActivity.deliverableTypes || [],
            assessmentCriteria: existingActivity.assessmentCriteria || [],
            expectedOutcomes: existingActivity.expectedOutcomes || [],
            activityState: existingActivity.activityState || ActivityState.Published, // default null/undefined activity state to published for existing data
            scheduledDate: existingActivity.scheduledDate || undefined,
            descriptionFormat: existingActivity.descriptionFormat,
            ...(existingActivity.hasReviewFor && { hasReviewFor: existingActivity.hasReviewFor }),
          }
        : null,
    [existingActivity, normalizedObjectives]
  );

  const handleSave = async (activityData) => {
    try {
      await onSave({
        ...activityData,
        id: existingActivity?.id,
      });

      // Call onClose after a successful save to properly reset the modal state
      handleClose();
    } catch (error) {
      console.error("Error saving activity:", error);
      throw error;
    }
  };

  const handleClose = (): void => {
    onClose();
  };

  if (visible && isLoading) {
    return (
      <CustomModal visible={visible} onRequestClose={onClose}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#0000ff" />
          <CustomText text="Loading activity..." size="m" weight="bold" useTranslationText={false} />
        </View>
      </CustomModal>
    );
  }

  return (
    <ActivityFormContainer
      visible={visible}
      onClose={handleClose}
      onSave={handleSave}
      error={error}
      isSubmitting={isSubmitting}
      existingActivity={formattedActivityData}
      courseId={courseId}
      key={visible ? "modal-open" : "modal-closed"}
      activeModuleData={activeModuleData}
    />
  );
};

export default EditActivityModalContainer;

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
});
